import { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react'
import { fetchScreenDashboardData, DashboardData } from 'src/admin/core/services/screens/screenDashboardService'
import { getAppBarState } from 'src/store'

interface ScreenDashboardContextValue {
  /** Dashboard data loaded from service */
  dashboardData: DashboardData | null
  /** Loading state */
  loading: boolean
  /** Error message if loading failed */
  error: string | null
  /** Currently selected consortium filter */
  selectedConsortium: string
  /** Update the consortium filter */
  setSelectedConsortium: (value: string) => void
  /** Reload data from service */
  refreshData: () => Promise<void>
  /** Auto-refresh interval in minutes (0 for off) */
  autoRefreshInterval: number
  /** Set auto-refresh interval */
  setAutoRefreshInterval: (value: number) => void
  /** Selected screen configuration ID for filtering */
  selectedScreenGroupId: string | null
  /** Update selected screen configuration ID */
  setSelectedScreenGroupId: (value: string | null) => void
}

const ScreenDashboardContext = createContext<ScreenDashboardContextValue | null>(null)

export const ScreenDashboardProvider = ({ children }: { children: ReactNode }) => {
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedConsortium, setSelectedConsortium] = useState<string>('')
  const [autoRefreshInterval, setAutoRefreshInterval] = useState<number>(5)
  const [selectedScreenGroupId, setSelectedScreenGroupId] = useState<string | null>(null)

  const { userEnterprise } = getAppBarState().appState

  const refreshData = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await fetchScreenDashboardData(userEnterprise?.id)
      setDashboardData(data)
      // Default consortium filter to data consortium name
      setSelectedConsortium((prev) => prev || data.consortium_name)
    } catch (err: any) {
      setError(err.message || 'Error loading dashboard data')
    } finally {
      setLoading(false)
    }
  }, [userEnterprise?.id])

  useEffect(() => {
    refreshData()
  }, [refreshData])

  useEffect(() => {
    if (autoRefreshInterval <= 0) return
    const intervalMs = autoRefreshInterval * 60 * 1000
    const timerId = setInterval(() => {
      refreshData()
    }, intervalMs)
    return () => clearInterval(timerId)
  }, [autoRefreshInterval, refreshData])

  return (
    <ScreenDashboardContext.Provider
      value={{
        dashboardData,
        loading,
        error,
        selectedConsortium,
        setSelectedConsortium,
        refreshData,
        autoRefreshInterval,
        setAutoRefreshInterval,
        selectedScreenGroupId,
        setSelectedScreenGroupId,
      }}
    >
      {children}
    </ScreenDashboardContext.Provider>
  )
}

export const useScreenDashboardContext = (): ScreenDashboardContextValue => {
  const ctx = useContext(ScreenDashboardContext)
  if (!ctx) throw new Error('useScreenDashboardContext must be used inside <ScreenDashboardProvider>')
  return ctx
}
